import { getCoverageLinesText } from '@common/utils';
import { isEmpty } from 'lodash';
import moment from 'moment/moment';
import {
  Chip,
  darken,
  Divider,
  Highlight,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  TypographyProps,
} from '@common-components';
import { FastForward, Warning } from '@icons';
import { SubmissionStatus } from 'enums';
import { approvalStatusConfig } from 'enums/type-mappers/approval-status-config';
import { clearanceOutputConfig } from 'enums/type-mappers/clearance-output-config';
import { getTriageOutputLabel } from 'enums/type-mappers/triage-output-config';
import { CarrierSubmission } from 'types';
import { getTimezoneLessDisplayDate, hasMinutesPassed, timeSince, truncateMultiLineText, uniteStyles } from 'utils';
import IconBadge from 'components/IconBadge';
import { ProcessingAnimation } from 'components/ProcessingAnimation';
import { QuoteCoverageLines } from 'broker/components/common/QuoteCoverageLines';
import ComingSoonChip from './ComingSoonChip';
import { QueueColumns, queueColumnsConfig } from './config';

const OVERDUE_THRESHOLD_HOURS = 72;

interface QueueTableItemProps {
  item: CarrierSubmission;
  isInsuredNameExtracted?: boolean;
  isCoverageLinesExtracted?: boolean;
  isEffectiveDateExtracted?: boolean;
  isBrokerNameExtracted?: boolean;
  searchInput?: string;
  onClick?: () => void;
}

const getExtractionTypographyProps = (
  isExtracted: boolean,
  variant: TypographyProps['variant'] = 'body2',
): Pick<TypographyProps, 'variant' | 'color'> =>
  isExtracted ? { variant: `${variant}Bold` as TypographyProps['variant'], color: 'green.600' } : { variant };

const getExtractionSxProps = (isExtracted: boolean): SxProps<Theme> =>
  isExtracted ? { span: { bgcolor: 'green.100' } } : {};

export default function QueueTableItem({
  item,
  isInsuredNameExtracted,
  isCoverageLinesExtracted,
  isEffectiveDateExtracted,
  isBrokerNameExtracted,
  searchInput = '',
  onClick,
}: QueueTableItemProps) {
  const { submission } = item;
  const isProcessing =
    submission?.status === SubmissionStatus.Processing && !hasMinutesPassed(submission.createdAt, 24);

  return (
    <>
      <Stack
        px={2}
        gap={1}
        direction="row"
        alignItems="stretch"
        height={72}
        position="relative"
        onClick={onClick}
        sx={{
          cursor: onClick ? 'pointer' : 'default',
          ':hover': onClick
            ? {
                bgcolor: (theme) => darken(theme.palette.common.white, 0.02),
              }
            : undefined,
        }}
      >
        {isProcessing && <ProcessingAnimation />}
        <Stack flex={queueColumnsConfig[QueueColumns.Received].flex} justifyContent="center">
          <Stack direction="row" gap={1} alignItems="center">
            {submission?.createdAt &&
              moment().diff(moment(submission.createdAt), 'hours') >= OVERDUE_THRESHOLD_HOURS && (
                <IconBadge size="extraSmall" IconComponent={Warning} bgcolor="warning.main" />
              )}
            <Typography variant="body2" noWrap>
              {submission?.createdAt ? timeSince(submission.createdAt) : ''}
            </Typography>
          </Stack>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.InsuredName].flex} justifyContent="center" overflow="hidden">
          <Typography
            {...getExtractionTypographyProps(!!isInsuredNameExtracted)}
            overflow="hidden"
            sx={uniteStyles({ ...truncateMultiLineText(2) }, getExtractionSxProps(!!isInsuredNameExtracted))}
          >
            <Highlight searchWords={[searchInput]} textToHighlight={submission?.insuredName ?? ''} />
          </Typography>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.Agent].flex} justifyContent="center" overflow="hidden">
          <Typography
            {...getExtractionTypographyProps(!!isBrokerNameExtracted)}
            sx={uniteStyles({ ...truncateMultiLineText(2) }, getExtractionSxProps(!!isBrokerNameExtracted))}
          >
            <Highlight searchWords={[searchInput]} textToHighlight={item.agentName ?? ''} />
          </Typography>
          <Typography variant="caption2" color="text.secondary">
            {item.agentNumber ?? ''}
          </Typography>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.LineOfBusiness].flex} justifyContent="center" overflow="hidden">
          {!isEmpty(submission?.coverageLines) && (
            <Tooltip
              tooltipContent={QuoteCoverageLines({ coverageLines: submission?.coverageLines || [], searchInput })}
            >
              <Typography
                {...getExtractionTypographyProps(!!isCoverageLinesExtracted, 'caption')}
                component="div"
                sx={uniteStyles(
                  { display: 'inline-block', ...truncateMultiLineText(2) },
                  getExtractionSxProps(!!isCoverageLinesExtracted),
                )}
              >
                <Highlight
                  searchWords={[searchInput]}
                  textToHighlight={getCoverageLinesText(submission?.coverageLines) || ''}
                />
              </Typography>
            </Tooltip>
          )}
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.ApprovalStatus].flex} justifyContent="center" alignItems="start">
          <Stack>
            {item.approvalStatus?.status && (
              <Tooltip
                tooltipContent={item.approvalStatus.reason || ''}
                enabled={!!item.approvalStatus.reason}
                arrow
                placement="right"
              >
                <Chip
                  size="small"
                  color={approvalStatusConfig[item.approvalStatus.status].color}
                  label={approvalStatusConfig[item.approvalStatus.status].label}
                />
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.EffectiveDate].flex} justifyContent="center">
          <Typography
            {...getExtractionTypographyProps(!!isEffectiveDateExtracted)}
            noWrap
            sx={getExtractionSxProps(!!isEffectiveDateExtracted)}
          >
            <span>{submission?.dueDate ? getTimezoneLessDisplayDate(submission.dueDate) : ''}</span>
          </Typography>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.ClearanceOutput].flex} justifyContent="center">
          {item.clearanceOutput && (
            <Chip
              size="small"
              color={clearanceOutputConfig[item.clearanceOutput].color}
              label={clearanceOutputConfig[item.clearanceOutput].label}
            />
          )}
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.TriageOutput].flex} justifyContent="center">
          {item.triageOutput ? (
            <Typography variant="body2" noWrap>
              <Highlight searchWords={[searchInput]} textToHighlight={getTriageOutputLabel(item.triageOutput)} />
            </Typography>
          ) : (
            <ComingSoonChip />
          )}
        </Stack>
        <Stack flex={queueColumnsConfig[QueueColumns.StpEnabled].flex} justifyContent="center">
          {item.stpEnabled ? (
            <FastForward fontSize="small" color="success" />
          ) : (
            <FastForward fontSize="small" color="info" />
          )}
        </Stack>
      </Stack>
      <Divider />
    </>
  );
}
